"use client";

import { FormDescription, FormField, FormItem, FormMessage } from "ui/components/form";
import { useNavigate, useParams } from "react-router-dom";

import AmountWithCurrencyInput from "components/form/amount-currency";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form";
import { FormLabel } from "components/form";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import classNames from "classnames";
import { dedicatedJobConfigSchema } from "@/modules/work/jobs/config/dedicated/schema";
import { useCreateDedicatedConfig, useUpdateDedicatedConfig } from "@/modules/work/jobs/config/dedicated/mutation";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormActions } from "components/form/actions";

const DedicatedConfigForm = ({ config }) => {
  const navigate = useNavigate();

  const { jobId } = useParams(); // Assuming jobId is passed in context

  const form = useForm({
    resolver: zodResolver(dedicatedJobConfigSchema),
    defaultValues: config || {},
  });

  const createMutation = useCreateDedicatedConfig();
  const updateMutation = useUpdateDedicatedConfig();

  const handleCreate = async (context) => {
    const { values } = context;

    const response = await createMutation.mutateAsync({
      jobId,
      values,
    });
    navigate(-1);
  };

  const handleUpdate = async (id,context) => {
    const { dirtyValues } = context;
    const response = await updateMutation.mutateAsync({
      id,
      values: dirtyValues,
    });
    navigate(-1);
  };

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      itemId={config?.id || "+"}
    >
      {({ isCreating, createItem, isUpdating, updateItem, isDirty, isNewItem }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (isNewItem) {
              createItem();
            } else {
              updateItem();
            }
          },
          { preventDefault: true },
        );
        return (
          <div className={classNames("p-6 max-w-md space-y-6")}>
            <AmountWithCurrencyInput
              form={form}
              amountKey="cost"
              amountLabel="Cost"
              collection="dedicated_job_config"
              isNewItem={isNewItem}
            />
            <FormField
              control={form.control}
              name="paid_leaves"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                    collection="dedicated_job_config"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={true}>Paid Leave</FormLabel>

                    <Input
                      type="number"
                      step="0.5"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    />
                  </PermissionControlledInput>
                  <FormDescription>
                    Number of paid leaves allowed for this dedicated allocation.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
              collection="dedicated_job_config"
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              createItem={createItem}
              updateItem={updateItem}
              isDirty={isDirty}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default DedicatedConfigForm;
